import React, { useState, useEffect, createContext, useContext, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// Lazy-loaded Pages
const LoginPage = lazy(() => import("./pages/LoginPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const MasterDataPage = lazy(() => import("./pages/MasterDataPage"));
const ProductControlPage = lazy(() => import("./pages/ProductControlPage"));
const ProductStyleLibraryPage = lazy(() => import("./pages/ProductStyleLibraryPage"));
const TaskManagementPage = lazy(() => import("./pages/TaskManagementPage"));
const TargetsProjectionsPage = lazy(() => import("./pages/TargetsProjectionsPage"));
const AdsCommandCenterPage = lazy(() => import("./pages/AdsCommandCenterPage"));
const SystemHealthPage = lazy(() => import("./pages/SystemHealthPage"));
const BusinessIntelligencePage = lazy(() => import("./pages/BusinessIntelligencePage"));
const FabricInventoryPage = lazy(() => import("./pages/FabricInventoryPage"));
const CuttingOrderPage = lazy(() => import("./pages/CuttingOrderPage"));
const BundleManagementPage = lazy(() => import("./pages/BundleManagementPage"));
const ScannerPage = lazy(() => import("./pages/ScannerPage"));
const ProductionPage = lazy(() => import("./pages/ProductionPage"));
const QualityControlPage = lazy(() => import("./pages/QualityControlPage"));
const FinishedInventoryPage = lazy(() => import("./pages/FinishedInventoryPage"));
const DispatchPage = lazy(() => import("./pages/DispatchPage"));
const AIAnalyticsPage = lazy(() => import("./pages/AIAnalyticsPage"));
const KarigarPerformancePage = lazy(() => import("./pages/KarigarPerformancePage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const BatchImportPage = lazy(() => import("./pages/BatchImportPage"));
const AlertsPage = lazy(() => import("./pages/AlertsPage"));
const ProductionPlanningPage = lazy(() => import("./pages/ProductionPlanningPage"));
const FabricConsumptionPage = lazy(() => import("./pages/FabricConsumptionPage"));
const FabricAlertPage = lazy(() => import("./pages/FabricAlertPage"));
const TransferPage = lazy(() => import("./pages/TransferPage"));
const AdvancePage = lazy(() => import("./pages/AdvancePage"));
const BulkLabelPrintingPage = lazy(() => import("./pages/BulkLabelPrintingPage"));
const WorkerEfficiencyPage = lazy(() => import("./pages/WorkerEfficiencyPage"));
const GlobalSearchPage = lazy(() => import("./pages/GlobalSearchPage"));
const FabricReceivePage = lazy(() => import("./pages/FabricReceivePage"));
const CuttingEntryPage = lazy(() => import("./pages/CuttingEntryPage"));
const MachineManagementPage = lazy(() => import("./pages/MachineManagementPage"));
const KarigarDashboardPage = lazy(() => import("./pages/KarigarDashboardPage"));
const CuttingIssuePage = lazy(() => import("./pages/CuttingIssuePage"));
const ProductionReceivePage = lazy(() => import("./pages/ProductionReceivePage"));
const UrgentEntryPage = lazy(() => import("./pages/UrgentEntryPage"));
const DhagaCuttingEntryPage = lazy(() => import("./pages/DhagaCuttingEntryPage"));
const LabelTakaGatherEntryPage = lazy(() => import("./pages/LabelTakaGatherEntryPage"));
const FlatEntryPage = lazy(() => import("./pages/FlatEntryPage"));
const AlterEntryPage = lazy(() => import("./pages/AlterEntryPage"));
const AnalyticsDashboardPage = lazy(() => import("./pages/AnalyticsDashboardPage"));
const MilestonesPage = lazy(() => import("./pages/MilestonesPage"));
const KarigarPaymentPage = lazy(() => import("./pages/KarigarPaymentPage"));
const KarigarPayoutDebugPage = lazy(() => import("./pages/KarigarPayoutDebugPage"));
const OpeningBalanceAdminPage = lazy(() => import("./pages/OpeningBalanceAdminPage"));
const StyleRateAuditPage = lazy(() => import("./pages/StyleRateAuditPage"));
const BundleAuditPage = lazy(() => import("./pages/BundleAuditPage"));const BulkDataUploadPage = lazy(() => import("./pages/BulkDataUploadPage"));
const SecuritySettingsPage = lazy(() => import("./pages/SecuritySettingsPage"));
const PerformanceIndexManagerPage = lazy(() => import("./pages/PerformanceIndexManagerPage"));
const HRPayrollDashboard = lazy(() => import("./pages/hr-payroll/HRPayrollPages"));
const HRPayrollPagesModule = () => import("./pages/hr-payroll/HRPayrollPages");
const HRPayrollEmployees = lazy(() => import("./pages/hr-payroll/EmployeeMasterPage"));
const HRPayrollAttendance = lazy(() => import("./pages/hr-payroll/AttendanceUploadPage"));
const HRPayrollConfiguration = lazy(() => import("./pages/hr-payroll/SalaryConfigurationPage"));
const HRPayrollSalaryProcessing = lazy(() => import("./pages/hr-payroll/SalaryProcessingPage"));
const HRPayrollBankIntegrity = lazy(() => import("./pages/hr-payroll/BankIntegrityReportPage"));
const HRPayrollAdvance = lazy(() => import("./pages/hr-payroll/AdvanceManagementPage"));
const HRPayrollFinalSalary = lazy(() => import("./pages/hr-payroll/FinalSalaryReport"));
const HRPayrollAnalyticsPage = lazy(() => import("./pages/hr-payroll/PayrollAnalytics"));
const HRPayrollHolidayCalendar = lazy(() => import("./pages/hr-payroll/HolidayCalendarPage"));
const HRPayrollHistoricalImport = lazy(() => import("./pages/hr-payroll/HistoricalImport"));

// Mobile V2 (Stitch UI preview) — completely isolated bundle.
const MobileV2Layout = lazy(() => import("./mobile-v2/MobileV2Layout"));
const MobileV2Guard = lazy(() => import("./mobile-v2/MobileV2Guard"));
const MobileV2DashboardPage = lazy(() => import("./mobile-v2/pages/DashboardPage"));
const MobileV2Placeholder = lazy(() => import("./mobile-v2/pages/Placeholder"));
const MobileV2RolloutPage = lazy(() => import("./mobile-v2/pages/UIRolloutManagerPage"));
const HRPayrollReports = lazy(() => HRPayrollPagesModule().then(m => ({ default: m.HRPayrollReports })));
const HRPayrollAnalytics = lazy(() => HRPayrollPagesModule().then(m => ({ default: m.HRPayrollAnalytics })));
const OperatorErrorPage = lazy(() => import("./pages/OperatorErrorPage"));
const DataResetPage = lazy(() => import("./pages/DataResetPage"));
const KarigarAvailabilityPage = lazy(() => import("./pages/KarigarAvailabilityPage"));
const UnicommercePage = lazy(() => import("./pages/UnicommercePage"));
const ChannelDashboardPage = lazy(() => import("./pages/ChannelDashboardPage"));
const UniwareReconciliationPage = lazy(() => import("./pages/UniwareReconciliationPage"));
const KarigarSettingsPage = lazy(() => import("./pages/KarigarSettingsPage"));
const ProcessTransferControlPage = lazy(() => import("./pages/ProcessTransferControlPage"));
const WorkerTransferPage = lazy(() => import("./pages/WorkerTransferPage"));
const ProcessTransferReportsPage = lazy(() => import("./pages/ProcessTransferReportsPage"));
const StyleWorkMappingPage = lazy(() => import("./pages/StyleWorkMappingPage"));
const BundleWorkPage = lazy(() => import("./pages/BundleWorkPage"));
const DailyProductionPage = lazy(() => import("./pages/DailyProductionPage"));
const SalesAnalyticsPage = lazy(() => import("./pages/SalesAnalyticsPage"));
const ModuleDisabledPage = lazy(() => import("./pages/ModuleDisabledPage"));
const KarigarLeavesPage = lazy(() => import("./pages/KarigarLeavesPage"));
const PressMasterPage = lazy(() => import("./pages/PressMasterPage"));
const PressEntryPage = lazy(() => import("./pages/PressEntryPage"));
const PressReportPage = lazy(() => import("./pages/PressReportPage"));
const PressDashboardPage = lazy(() => import("./pages/PressDashboardPage"));
const PressPaymentPage = lazy(() => import("./pages/PressPaymentPage"));
const BestEmployeePage = lazy(() => import("./pages/BestEmployeePage"));
const OtpDashboard = lazy(() => import("./pages/otp/OtpDashboard"));
const OtpMailAccounts = lazy(() => import("./pages/otp/OtpMailAccounts"));
const OtpAuditLogs = lazy(() => import("./pages/otp/OtpAuditLogs"));
const PicklistListPage = lazy(() => import("./pages/picklist/PicklistListPage"));
const PicklistDetailPage = lazy(() => import("./pages/picklist/PicklistDetailPage"));
const PicklistFinalViewPage = lazy(() => import("./pages/picklist/PicklistFinalViewPage"));
const ReturnScanPage = lazy(() => import("./pages/ReturnScanPage"));
const BulkPutawayExportPage = lazy(() => import("./pages/BulkPutawayExportPage"));
const ReturnAnalyticsPage = lazy(() => import("./pages/ReturnAnalyticsPage"));
import { OfflineSyncManager } from "./components/OfflineSyncManager";
import { createOfflineAwareApi } from "./lib/offlineApi";

// Loading fallback with skeleton
const PageLoader = () => (
  <div className="min-h-screen bg-background p-6 space-y-4">
    <div className="h-8 w-48 bg-muted rounded-sm animate-pulse" />
    <div className="h-4 w-72 bg-muted/60 rounded-sm animate-pulse" />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
      {[1,2,3].map(i => <div key={i} className="h-32 bg-muted/40 rounded-sm animate-pulse" />)}
    </div>
    <div className="h-64 bg-muted/30 rounded-sm animate-pulse mt-4" />
  </div>
);

// Error Boundary for lazy-loaded pages
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    console.error("Page load error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen bg-background">
          <div className="text-center space-y-4">
            <p className="text-lg font-semibold text-destructive">Page Load Error</p>
            <p className="text-sm text-muted-foreground">Page load mein issue aaya. Retry karo.</p>
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// API Configuration - use relative path to avoid exposing backend host in JS bundle
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Register Service Worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {});
  });
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// API instance with auth + caching for master data
const apiCache = new Map();
const CACHE_TTL = 120000; // 2 minutes
// IMPORTANT: Only cache TRULY static reference data here. Anything that gets mutated
// frequently (orders, payments, entries, dynamic statuses) MUST NOT be cached client-side
// because the cache survives across mutations and shows stale data until TTL expires.
const CACHEABLE_PATHS = ["/styles", "/colors", "/sizes", "/karigars", "/machines", "/godowns", "/work-types"];

// Paths whose mutations should INVALIDATE related cache entries.
// Key = mutation URL prefix (after /api), Value = list of GET paths to invalidate.
const CACHE_INVALIDATION_RULES = {
  "/styles": ["/styles"],
  "/colors": ["/colors"],
  "/sizes": ["/sizes"],
  "/karigars": ["/karigars"],
  "/machines": ["/machines", "/karigars"],
  "/godowns": ["/godowns"],
  "/work-types": ["/work-types"],
};

export const createApiInstance = (token) => {
  const instance = axios.create({
    baseURL: API,
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (token) {
    instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  // Cache GET requests for master data only
  instance.interceptors.request.use((config) => {
    if (config.method === "get" && CACHEABLE_PATHS.some(p => config.url?.endsWith(p))) {
      const cacheKey = config.url;
      const cached = apiCache.get(cacheKey);
      if (cached && Date.now() - cached.ts < CACHE_TTL) {
        config.adapter = () => Promise.resolve({ data: cached.data, status: 200, statusText: "OK", headers: {}, config });
      }
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      const method = response.config.method;
      const url = response.config.url || "";

      // Cache successful GET responses for master data
      if (method === "get" && CACHEABLE_PATHS.some(p => url.endsWith(p))) {
        apiCache.set(url, { data: response.data, ts: Date.now() });
      }

      // Invalidate cache on mutations (POST/PUT/PATCH/DELETE)
      if (["post", "put", "patch", "delete"].includes(method)) {
        for (const [prefix, invalidatePaths] of Object.entries(CACHE_INVALIDATION_RULES)) {
          if (url.includes(prefix)) {
            invalidatePaths.forEach((p) => {
              for (const k of apiCache.keys()) {
                if (k.endsWith(p)) apiCache.delete(k);
              }
            });
          }
        }
      }
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem("agm_token");
        localStorage.removeItem("agm_user");
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  // Initialize state from localStorage synchronously
  const storedToken = localStorage.getItem("agm_token");
  const storedUser = localStorage.getItem("agm_user");
  
  const [user, setUser] = useState(() => {
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [token, setToken] = useState(storedToken);
  const [loading, setLoading] = useState(false);
  const [api, setApi] = useState(() => {
    return storedToken ? createOfflineAwareApi(createApiInstance(storedToken)) : null;
  });

  // No need for useEffect to load from localStorage anymore - already done in useState initializers
  // This effect can be used for any other initialization if needed

  const login = async (username, password, otpCode = null) => {
    try {
      const payload = { username, password };
      if (otpCode) payload.otp_code = otpCode;
      const response = await axios.post(`${API}/auth/login`, payload);

      // 2FA challenge - need OTP
      if (response.data.requires_2fa) {
        return { success: false, requires_2fa: true, username, password };
      }

      const { access_token, user: userData } = response.data;

      localStorage.setItem("agm_token", access_token);
      localStorage.setItem("agm_user", JSON.stringify(userData));

      setToken(access_token);
      setUser(userData);
      setApi(createOfflineAwareApi(createApiInstance(access_token)));

      toast.success(`Welcome back, ${userData.name}!`);
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const loginPress = async (username, password) => {
    try {
      const response = await axios.post(`${API}/press/login`, { username, password });
      const { access_token, operator, expires_in } = response.data;
      const userData = {
        id: operator.id,
        name: operator.name,
        username: operator.username,
        role: "press_operator",
        payout_type: operator.payout_type,
        rate_per_piece: operator.rate_per_piece,
        monthly_salary: operator.monthly_salary,
      };
      localStorage.setItem("agm_token", access_token);
      localStorage.setItem("agm_user", JSON.stringify(userData));
      if (expires_in) {
        localStorage.setItem("agm_token_expiry", String(Date.now() + expires_in * 1000));
      }
      setToken(access_token);
      setUser(userData);
      setApi(createOfflineAwareApi(createApiInstance(access_token)));
      toast.success(`Welcome, ${operator.name}!`);
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };


  const loginKarigar = async (machineNumber, password) => {
    try {
      const response = await axios.post(`${API}/karigar/login`, {
        machine_number: machineNumber,
        password: password,
      });
      const { access_token, user: userData, expires_in } = response.data;

      localStorage.setItem("agm_token", access_token);
      localStorage.setItem("agm_user", JSON.stringify(userData));
      if (expires_in) {
        localStorage.setItem("agm_token_expiry", String(Date.now() + expires_in * 1000));
      }

      setToken(access_token);
      setUser(userData);
      setApi(createOfflineAwareApi(createApiInstance(access_token)));

      toast.success(`Welcome, ${userData.name}!`);
      // Auto-logout only if token is very short (< 1 hour) — removed for shift-based work
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    if (!api || typeof api.post !== "function") {
      const msg = "Session expired. Please login again.";
      toast.error(msg);
      return { success: false, error: msg };
    }
    try {
      await api.post("/auth/register", userData);
      toast.success("User registered successfully");
      return { success: true };
    } catch (error) {
      const status = error?.response?.status;
      let message = error?.response?.data?.detail || "Registration failed";
      if (status === 401) message = "Session expired. Please login again.";
      else if (status === 403) message = error?.response?.data?.detail || "Permission denied: user_create required";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem("agm_token");
    localStorage.removeItem("agm_user");
    localStorage.removeItem("agm_token_expiry");
    if (window._autoLogoutTimer) clearTimeout(window._autoLogoutTimer);
    setToken(null);
    setUser(null);
    setApi(null);
    toast.success("Logged out successfully");
  };

  const setupAutoLogout = (ms) => {
    if (window._autoLogoutTimer) clearTimeout(window._autoLogoutTimer);
    // Only auto-logout if remaining time is less than 1 hour (safety net)
    if (ms < 3600000) {
      window._autoLogoutTimer = setTimeout(() => {
        toast.error("Session expired. Please login again.");
        logout();
      }, ms);
    }
  };

  // On app load: check if karigar token is expired
  useEffect(() => {
    const expiry = localStorage.getItem("agm_token_expiry");
    if (expiry && user?.role === "karigar") {
      const remaining = parseInt(expiry) - Date.now();
      if (remaining <= 0) {
        logout();
      } else {
        setupAutoLogout(remaining);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === "string") return user.role === roles;
    return roles.includes(user.role);
  };

  // Page-key → set of child permission keys that also grant sidebar/route access
  // to that parent page. If user has ANY of these, the parent page becomes visible.
  // NOTE: This only unlocks navigation shell. Individual actions inside each page
  // are still gated by their specific permission checks (backend + UI).
  const PAGE_CHILD_PERMS = {
    "product-control": [
      "product_control_access",
      "size_chart_view", "size_chart_create", "size_chart_edit_draft",
      "size_chart_edit_published", "size_chart_publish", "size_chart_delete",
      "size_chart_export", "size_chart_copy", "size_chart_link_shared",
      "size_chart_unlink_shared", "size_chart_shared_manage",
      "size_chart_template_view", "size_chart_template_manage",
      "size_chart_audit_view",
    ],
    "picklist-list": [
      "picklist-operator-1", "picklist-operator-2", "picklist-final-view",
    ],
  };

  const hasPermission = (pageKey) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (!user.permissions || user.permissions.length === 0) return true;
    if (user.permissions.includes(pageKey)) return true;
    // Child-perm → parent-page visibility (RBAC hierarchy, Feb 2026)
    const children = PAGE_CHILD_PERMS[pageKey];
    if (children && children.some((c) => user.permissions.includes(c))) return true;
    return false;
  };

  const invalidateCache = (path) => {
    if (path) {
      for (const key of apiCache.keys()) {
        if (key.includes(path)) apiCache.delete(key);
      }
    } else {
      apiCache.clear();
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, api, apiReady: !!(api && typeof api.get === "function"), loading, login, loginKarigar, loginPress, register, logout, hasRole, hasPermission, invalidateCache }}>
      {children}
    </AuthContext.Provider>
  );
};

import { PAGE_REGISTRY } from "./config/pageConfig";

// Karigar-allowed paths
const KARIGAR_ALLOWED_PATHS = ["/karigar-dashboard", "/bundle-work"];

// Press operator allowed paths
const PRESS_ALLOWED_PATHS = ["/press-dashboard"];

// Find the first permitted page for a user
export function getFirstAllowedPage(user) {
  if (!user) return "/login";
  if (user.role === "karigar") return "/karigar-dashboard";
  if (user.role === "press_operator") return "/press-dashboard";
  if (user.role === "admin") return "/dashboard";

  // For operators: find first page they have permission for
  const perms = user.permissions || [];
  if (perms.length === 0) return "/dashboard"; // no restrictions = all access

  // Try dashboard first
  if (perms.includes("dashboard")) return "/dashboard";

  // Child-perm → parent-page mapping (must mirror hasPermission logic below)
  const PAGE_CHILD_PERMS_INIT = {
    "product-control": [
      "product_control_access",
      "size_chart_view", "size_chart_create", "size_chart_edit_draft",
      "size_chart_edit_published", "size_chart_publish", "size_chart_delete",
      "size_chart_export", "size_chart_copy", "size_chart_link_shared",
      "size_chart_unlink_shared", "size_chart_shared_manage",
      "size_chart_template_view", "size_chart_template_manage",
      "size_chart_audit_view",
    ],
    "picklist-list": ["picklist-operator-1", "picklist-operator-2", "picklist-final-view"],
  };

  // Find the first permitted page from the registry (including child-perm mapping).
  // Role list is a fallback filter — if user has explicit permission, we honour it.
  for (const page of PAGE_REGISTRY) {
    const hasDirect = perms.includes(page.key);
    const kids = PAGE_CHILD_PERMS_INIT[page.key];
    const hasChild = kids && kids.some((c) => perms.includes(c));
    if (hasDirect || hasChild) {
      // Explicit permission granted — allow regardless of role list
      return page.path;
    }
  }
  return "/dashboard"; // fallback
}

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, pageKey }) => {
  const { user, loading, api, hasRole, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Ensure both user AND api are ready before rendering protected content
  if (!user || !api) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Karigar role restriction - only allow karigar dashboard (Mobile V2 tree bypasses this;
  // its own MobileV2Guard reads the rollout config to decide access).
  if (user.role === "karigar" && !KARIGAR_ALLOWED_PATHS.includes(location.pathname) && !location.pathname.startsWith("/mobile-v2")) {
    return <Navigate to="/karigar-dashboard" replace />;
  }

  // Press operator restriction
  if (user.role === "press_operator" && !PRESS_ALLOWED_PATHS.includes(location.pathname) && !location.pathname.startsWith("/mobile-v2")) {
    return <Navigate to="/press-dashboard" replace />;
  }

  if (allowedRoles && !hasRole(allowedRoles)) {
    return <Navigate to={getFirstAllowedPage(user)} replace />;
  }

  // Check page-level permissions (admin bypassed in hasPermission)
  if (pageKey && !hasPermission(pageKey)) {
    const fallback = getFirstAllowedPage(user);
    // Prevent infinite redirect: if fallback is current path, show access denied
    if (fallback === location.pathname) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center space-y-3">
            <p className="text-lg font-semibold text-destructive">Access Denied</p>
            <p className="text-sm text-muted-foreground">Is page ki permission nahi hai. Admin se contact karo.</p>
          </div>
        </div>
      );
    }
    return <Navigate to={fallback} replace />;
  }

  return children;
};

// Default redirect based on role
const DefaultRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={getFirstAllowedPage(user)} replace />;
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-background">
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            {/* ===== Mobile V2 (Stitch preview) — isolated tree, admin-gated by default ===== */}
            <Route
              path="/mobile-v2"
              element={
                <ProtectedRoute>
                  <MobileV2Guard>
                    <MobileV2Layout />
                  </MobileV2Guard>
                </ProtectedRoute>
              }
            >
              <Route index element={<MobileV2DashboardPage />} />
              <Route path="scan" element={<MobileV2Placeholder title="Scan" description="Barcode / QR scan flow." icon="barcode_scanner" stitchRef="scan_receive_final_polish" testId="mv2-page-scan" />} />
              <Route path="bundles" element={<MobileV2Placeholder title="Bundles" description="Bundle management." icon="inventory_2" stitchRef="bundle_management_final_polish" testId="mv2-page-bundles" />} />
              <Route path="payout" element={<MobileV2Placeholder title="Payout" description="Karigar payout history." icon="payments" stitchRef="karigar_payout_detail_final_polish" testId="mv2-page-payout" />} />
              <Route path="hr" element={<MobileV2Placeholder title="HR" description="HR dashboard." icon="groups" stitchRef="hr_dashboard" testId="mv2-page-hr" />} />
              <Route path="cutting" element={<MobileV2Placeholder title="Cutting" description="Cutting operations." icon="content_cut" stitchRef="cutting_dashboard_final_polish" testId="mv2-page-cutting" />} />
              <Route path="workers" element={<MobileV2Placeholder title="Workers" description="Worker search & detail." icon="engineering" stitchRef="employee_master_list" testId="mv2-page-workers" />} />
              <Route path="picklist" element={<MobileV2Placeholder title="Picklist" description="Picklist final view." icon="checklist" stitchRef="picklist_final_view" testId="mv2-page-picklist" />} />
              <Route path="packing" element={<MobileV2Placeholder title="Packing" description="Packing scan." icon="package_2" stitchRef="packing_scan" testId="mv2-page-packing" />} />
              <Route path="returns" element={<MobileV2Placeholder title="Returns" description="Return / RTO scan." icon="assignment_return" stitchRef="return_rto_scan_final_polish" testId="mv2-page-returns" />} />
              <Route path="attendance" element={<MobileV2Placeholder title="Attendance" description="Attendance upload & preview." icon="event_available" stitchRef="attendance_upload_preview" testId="mv2-page-attendance" />} />
              <Route path="salary" element={<MobileV2Placeholder title="Salary" description="Salary processing preview." icon="payments" stitchRef="salary_processing_preview" testId="mv2-page-salary" />} />
              <Route path="reports" element={<MobileV2Placeholder title="Reports" description="Payroll & production reports." icon="insights" stitchRef="payroll_reports_hub" testId="mv2-page-reports" />} />
              <Route path="more" element={<MobileV2Placeholder title="More" description="Additional menu." icon="more_horiz" stitchRef="final_app_map_navigation_guide" testId="mv2-page-more" />} />
              <Route path="settings/rollout" element={<MobileV2RolloutPage />} />
            </Route>

            
            <Route
              path="/karigar-dashboard"
              element={
                <ProtectedRoute>
                  <KarigarDashboardPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute pageKey="dashboard">
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/master-data"
              element={
                <ProtectedRoute pageKey="master-data">
                  <MasterDataPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-control"
              element={
                <ProtectedRoute pageKey="product-control">
                  <ProductControlPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-control/style-library"
              element={
                <ProtectedRoute pageKey="product-control">
                  <ProductStyleLibraryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/task-management"
              element={
                <ProtectedRoute pageKey="task-management">
                  <TaskManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/targets-projections"
              element={
                <ProtectedRoute pageKey="targets-projections">
                  <TargetsProjectionsPage />
                </ProtectedRoute>
              }
            />
            {/* Ads Command Center disabled Aug 27, 2026 — Redirect to dashboard */}
            <Route
              path="/ads-command-center"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route
              path="/system-health"
              element={
                <ProtectedRoute>
                  <SystemHealthPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-intelligence"
              element={
                <ProtectedRoute pageKey="business-intelligence">
                  <BusinessIntelligencePage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/fabric-inventory"
              element={
                <ProtectedRoute pageKey="fabric-inventory">
                  <FabricInventoryPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/cutting-orders"
              element={
                <ProtectedRoute pageKey="cutting-orders">
                  <CuttingOrderPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/bundles"
              element={
                <ProtectedRoute pageKey="bundles">
                  <BundleManagementPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/scanner"
              element={
                <ProtectedRoute>
                  <ScannerPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/production"
              element={
                <ProtectedRoute pageKey="production">
                  <ProductionPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/cutting-issue"
              element={
                <ProtectedRoute pageKey="cutting-issue">
                  <CuttingIssuePage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/production-receive"
              element={
                <ProtectedRoute pageKey="production-receive">
                  <ProductionReceivePage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/quality-control"
              element={
                <ProtectedRoute pageKey="quality-control">
                  <QualityControlPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/urgent-entry"
              element={
                <ProtectedRoute pageKey="urgent-entry">
                  <UrgentEntryPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/dhaga-cutting"
              element={
                <ProtectedRoute pageKey="dhaga-cutting">
                  <DhagaCuttingEntryPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/label-taka-gather"
              element={
                <ProtectedRoute pageKey="label-taka-gather">
                  <LabelTakaGatherEntryPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/flat-entry"
              element={
                <ProtectedRoute pageKey="flat-entry">
                  <FlatEntryPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/alter-entry"
              element={
                <ProtectedRoute pageKey="alter-entry">
                  <AlterEntryPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/analytics-dashboard"
              element={
                <ProtectedRoute pageKey="analytics-dashboard">
                  <AnalyticsDashboardPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/milestones"
              element={
                <ProtectedRoute pageKey="milestones">
                  <MilestonesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/karigar-payments"
              element={
                <ProtectedRoute pageKey="karigar-payments">
                  <KarigarPaymentPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/karigar-payout-debug"
              element={
                <ProtectedRoute pageKey="karigar-payout-debug">
                  <KarigarPayoutDebugPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/opening-balance-admin"
              element={
                <ProtectedRoute pageKey="opening-balance-admin">
                  <OpeningBalanceAdminPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/style-rate-audit"
              element={
                <ProtectedRoute pageKey="style-rate-audit">
                  <StyleRateAuditPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/bundle-audit"
              element={
                <ProtectedRoute pageKey="bundle-audit">
                  <BundleAuditPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/security"
              element={
                <ProtectedRoute allowedRoles={["admin", "production_manager"]}>
                  <SecuritySettingsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/performance-index-manager"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <PerformanceIndexManagerPage />
                </ProtectedRoute>
              }
            />

            <Route path="/hr-payroll" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollDashboard /></ProtectedRoute>} />
            <Route path="/hr-payroll/employees" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollEmployees /></ProtectedRoute>} />
            <Route path="/hr-payroll/attendance" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollAttendance /></ProtectedRoute>} />
            <Route path="/hr-payroll/configuration" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollConfiguration /></ProtectedRoute>} />
            <Route path="/hr-payroll/salary-processing" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollSalaryProcessing /></ProtectedRoute>} />
            <Route path="/hr-payroll/bank-integrity" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollBankIntegrity /></ProtectedRoute>} />
            <Route path="/hr-payroll/advance-management" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollAdvance /></ProtectedRoute>} />
            <Route path="/hr-payroll/advance" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollAdvance /></ProtectedRoute>} />
            <Route path="/hr-payroll/final-salary" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollFinalSalary /></ProtectedRoute>} />
            <Route path="/hr-payroll/historical-import" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollHistoricalImport /></ProtectedRoute>} />
            <Route path="/hr-payroll/reports" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollReports /></ProtectedRoute>} />
            <Route path="/hr-payroll/analytics" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollAnalyticsPage /></ProtectedRoute>} />
            <Route path="/hr-payroll/holidays" element={<ProtectedRoute allowedRoles={["admin"]}><HRPayrollHolidayCalendar /></ProtectedRoute>} />
            
            <Route
              path="/bulk-upload"
              element={
                <ProtectedRoute pageKey="bulk-upload">
                  <BulkDataUploadPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operator-errors"
              element={
                <ProtectedRoute pageKey="operator-errors">
                  <OperatorErrorPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/finished-inventory"
              element={
                <ProtectedRoute pageKey="finished-inventory">
                  <FinishedInventoryPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/data-reset"
              element={
                <ProtectedRoute pageKey="data-reset">
                  <DataResetPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/karigar-availability"
              element={
                <ProtectedRoute pageKey="karigar-availability">
                  <KarigarAvailabilityPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/unicommerce"
              element={
                <ProtectedRoute pageKey="unicommerce">
                  <UnicommercePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/channel-dashboard"
              element={
                <ProtectedRoute pageKey="dashboard">
                  <ChannelDashboardPage />
                </ProtectedRoute>
              }            />

            <Route
              path="/unicommerce/reconciliation"
              element={
                <ProtectedRoute pageKey="unicommerce">
                  <UniwareReconciliationPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/karigar-settings"
              element={
                <ProtectedRoute pageKey="karigar-settings">
                  <KarigarSettingsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/process-transfer-control"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <ProcessTransferControlPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/process-transfers"
              element={
                <ProtectedRoute>
                  <WorkerTransferPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/process-transfer-reports"
              element={
                <ProtectedRoute allowedRoles={["admin", "production_manager", "supervisor"]}>
                  <ProcessTransferReportsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/style-work-mapping"
              element={
                <ProtectedRoute allowedRoles={["admin", "production_manager"]}>
                  <StyleWorkMappingPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/dispatch"
              element={
                <ProtectedRoute pageKey="dispatch">
                  <DispatchPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/ai-analytics"
              element={
                <ProtectedRoute pageKey="ai-analytics">
                  <ModuleDisabledPage moduleName="AI Analytics" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/bundle-work"
              element={
                <ProtectedRoute pageKey="bundle-work">
                  <BundleWorkPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/worker-performance"
              element={
                <ProtectedRoute pageKey="worker-performance">
                  <KarigarPerformancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/karigar-leaves"
              element={
                <ProtectedRoute pageKey="karigar-leaves" allowedRoles={["admin","production_manager"]}>
                  <KarigarLeavesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/press-master"
              element={
                <ProtectedRoute pageKey="press-master" allowedRoles={["admin","production_manager"]}>
                  <PressMasterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/press-entry"
              element={
                <ProtectedRoute pageKey="press-entry">
                  <PressEntryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/press-report"
              element={
                <ProtectedRoute pageKey="press-report" allowedRoles={["admin","production_manager"]}>
                  <PressReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/press-dashboard"
              element={
                <ProtectedRoute allowedRoles={["press_operator"]}>
                  <PressDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/press-payments"
              element={
                <ProtectedRoute pageKey="press-payments" allowedRoles={["admin","production_manager"]}>
                  <PressPaymentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/best-employee"
              element={
                <ProtectedRoute pageKey="best-employee" allowedRoles={["admin","production_manager"]}>
                  <BestEmployeePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/otp-center"
              element={
                <ProtectedRoute pageKey="otp-dashboard" allowedRoles={["admin","production_manager"]}>
                  <OtpDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/otp-center/accounts"
              element={
                <ProtectedRoute pageKey="otp-accounts" allowedRoles={["admin","production_manager"]}>
                  <OtpMailAccounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/otp-center/audit"
              element={
                <ProtectedRoute pageKey="otp-audit" allowedRoles={["admin"]}>
                  <OtpAuditLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/picklist"
              element={
                <ProtectedRoute pageKey="picklist-list" allowedRoles={["admin","production_manager","operator"]}>
                  <PicklistListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/picklist/:batchId"
              element={
                <ProtectedRoute pageKey="picklist-list" allowedRoles={["admin","production_manager","operator"]}>
                  <PicklistDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/picklist/:batchId/final-view"
              element={
                <ProtectedRoute pageKey="picklist-list" allowedRoles={["admin","production_manager","operator"]}>
                  <PicklistFinalViewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/return-scan"
              element={
                <ProtectedRoute pageKey="return-scan" allowedRoles={["admin","production_manager","operator"]}>
                  <ReturnScanPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bulk-putaway"
              element={
                <ProtectedRoute pageKey="bulk-putaway" allowedRoles={["admin","production_manager"]}>
                  <BulkPutawayExportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/return-analytics"
              element={
                <ProtectedRoute pageKey="return-analytics" allowedRoles={["admin","production_manager","operator"]}>
                  <ReturnAnalyticsPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/reports"
              element={
                <ProtectedRoute pageKey="reports">
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/batch-import"
              element={
                <ProtectedRoute pageKey="batch-import">
                  <BatchImportPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/alerts"
              element={
                <ProtectedRoute pageKey="alerts">
                  <AlertsPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/production-planning"
              element={
                <ProtectedRoute pageKey="production-planning">
                  <ProductionPlanningPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/daily-production"
              element={
                <ProtectedRoute pageKey="daily-production">
                  <DailyProductionPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/sales-analytics"
              element={
                <ProtectedRoute pageKey="sales-analytics">
                  <ModuleDisabledPage moduleName="Sales Analytics" />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/fabric-consumption"
              element={
                <ProtectedRoute pageKey="fabric-consumption">
                  <FabricConsumptionPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/fabric-alerts"
              element={
                <ProtectedRoute pageKey="fabric-alerts">
                  <FabricAlertPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/godown-transfer"
              element={
                <ProtectedRoute pageKey="godown-transfer">
                  <TransferPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/advance"
              element={
                <ProtectedRoute pageKey="advance">
                  <AdvancePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/bulk-labels"
              element={
                <ProtectedRoute pageKey="bulk-labels">
                  <BulkLabelPrintingPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/worker-efficiency"
              element={
                <ProtectedRoute pageKey="worker-efficiency">
                  <WorkerEfficiencyPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/search"
              element={
                <ProtectedRoute pageKey="search">
                  <GlobalSearchPage />
                </ProtectedRoute>
              }
            />
            
            
            <Route
              path="/fabric-receive"
              element={
                <ProtectedRoute pageKey="fabric-receive">
                  <FabricReceivePage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/cutting-entry"
              element={
                <ProtectedRoute pageKey="cutting-entry">
                  <CuttingEntryPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/machines"
              element={
                <ProtectedRoute pageKey="machines">
                  <MachineManagementPage />
                </ProtectedRoute>
              }
            />
            
            <Route path="/" element={<DefaultRedirect />} />
            <Route path="*" element={<DefaultRedirect />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
          
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: 'hsl(240 6% 10%)',
                color: 'hsl(0 0% 98%)',
                border: '1px solid hsl(240 4% 16%)',
              },
            }}
          />
          <OfflineSyncManager />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
